import React, { useEffect } from 'react'
import { Navigate, Outlet, useLocation, type Location } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import ServerUnavailablePage from '@/app/pages/ServerUnavailablePage'
import { clearSession, isAuthError, isServerUnavailable } from '@/common/lib/authSession'
import { resolvePostAuthRedirect } from '@/common/lib/safeRedirect'
import { useMe, type TAuth } from '../model/me'

type RedirectMode = 'append' | 'consume'
type GuardVariant = 'guest' | 'protected'

type GuardProps = React.PropsWithChildren<{
    rule: (auth: TAuth) => boolean
    admin?: boolean
    throwOutTo: string
    redirectMode: RedirectMode
    variant: GuardVariant
}>

const getDestination = (location: Location, throwOutTo: string, redirectMode: RedirectMode): string => {
    if (redirectMode === 'consume') {
        return resolvePostAuthRedirect(location.search, throwOutTo)
    }

    const redirectTarget = `${location.pathname}${location.search}`
    return redirectTarget ? `${throwOutTo}?redirect=${encodeURIComponent(redirectTarget)}` : throwOutTo
}

const Guard = ({ rule, throwOutTo, redirectMode, variant, children, admin }: GuardProps) => {
    const queryClient = useQueryClient()
    const { isError, isSuccess, error, me, isPending, isFetching } = useMe()
    const location = useLocation()
    const destination = getDestination(location, throwOutTo, redirectMode)
    const isCheckingAuth = isPending || isFetching

    useEffect(() => {
        if (!isError || isCheckingAuth || isServerUnavailable(error)) {
            return
        }

        if (isAuthError(error)) {
            clearSession(queryClient)
        }
    }, [error, isCheckingAuth, isError, queryClient])

    if (isCheckingAuth) {
        return null
    }

    if (isError) {
        if (isServerUnavailable(error)) {
            return <ServerUnavailablePage />
        }

        if (variant === 'guest') {
            return children
        }

        return <Navigate replace to={destination} />
    }

    if (isSuccess) {
        if (admin) {
            if (!me?.is_admin) {
                return <Navigate replace to={destination} />
            }
        } else if (rule(true)) {
            return <Navigate replace to={destination} />
        }

        return children
    }

    return null
}

type FinalGuardProps = Omit<GuardProps, 'rule' | 'redirectMode' | 'variant'>

const rules = {
    signedIn: (auth: TAuth) => auth,
    signedOut: (auth: TAuth) => !auth,
}

export const GuestGuard = (props: FinalGuardProps) => (
    <Guard rule={rules.signedIn} redirectMode='consume' variant='guest' {...props} />
)

export const UserGuard = (props: FinalGuardProps) => (
    <Guard rule={rules.signedOut} redirectMode='append' variant='protected' {...props} />
)

export const AdminGuard = (props: FinalGuardProps) => (
    <Guard rule={rules.signedOut} redirectMode='append' variant='protected' {...props} />
)

export const RegistrationGuard = () => <Outlet />

export const AppGuard = () => <Outlet />
